import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import {
  setMode,
  selectRecord,
  getTalentRegisterLinks,
  setCurrentPage,
  deleteTalentRegisterLink,
  setError,
  setSuccess,
  getTalentRegisterLinkDetail,
  resetForm,
  handleSortByHeader,
  setSearchQuery
} from "../../reducers/talentRegisterLink";
import parse from "html-react-parser";
import CompanyHeader from "components/CompanyHeader";
import CompanySideMenu from "components/CompanySideMenu";
import ReturnButton from "../../components/ReturnButton";
import CustomButton from "../../components/CustomButton";
import CustomPagination from "../../components/CustomPagination";
import RegistrationLinkPanel from "./components/RegistrationLinkPanel";
import Copyright from "../../components/Copyright";
import { getOptionsRelatedTalent, setTypeOptions, setGlobalSearchTalentFormData, setIsClickSearch, setHaveOtherConditionSearch } from "../../reducers/companyTalent";
import { formatDate, trimStringFields } from "../../utils/format";
import ConfirmPopup from "../../components/ConfirmPopup";
import CustomToast from "../../components/CustomToast";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import TextInput from "../../components/TextInput";
import { STAFF_TYPE } from "../../constants/index";

const RegistrationLinkManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fromMenu } = location.state || {};
  const dispatch = useDispatch();
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const { tableHeaders, registerLinkList, selectedRecord, error, success,
    sortBy, sortOrder, currentPage, pageSize, countItems, searchQuery } = useSelector((state: any) => state.talentRegisterLink);
  const { talentOptionsRelatedData, initSearchTalentForm } = useSelector((state: any) => state.companyTalent);
  const { isFromTagsList, isOpenSearchTagsPanel } = useSelector((state: any) => state.talentTag);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isOpenRegisterPanel, setIsOpenRegisterPanel] = useState(false);
  const [deletingItem, setDeletingItem] = useState({});

  const openPanel = (mode: "create" | "edit" | "view", record?: any) => {
    dispatch(setMode(mode));
    if(mode === "edit" || mode === "view") {
      dispatch(getTalentRegisterLinkDetail({id: Number(record?.id)}));
    } else {
      dispatch(resetForm());
    }
    setIsOpenRegisterPanel(true);
  };
  
  const handleClose = () => {
    setIsOpenRegisterPanel(false);
    dispatch(selectRecord(null));
    dispatch(resetForm());
  };

  const redirectToTalentList = (item) => {
    dispatch(setGlobalSearchTalentFormData({
      ...initSearchTalentForm,
      register_code: item?.code,
      withdrawn_talent_included: true,
    }))
    dispatch(setHaveOtherConditionSearch(true));
    dispatch(setIsClickSearch(true));
    navigate("/company/talent", { state: { isFromDetail: false } });
  }

  const fetchTalentRegisterLinks = (page=currentPage, search=searchQuery) => {
    let data = {
      page: page,
      pageSize: pageSize,
      sortBy: sortBy,
      sortOrder: sortOrder,
      searchQuery: search,
    }
    data = trimStringFields(data);
    dispatch(getTalentRegisterLinks(data));
    dispatch(setCurrentPage(page));
  }

  const handleChangePage = (page: number) => {
    dispatch(setCurrentPage(page))
    fetchTalentRegisterLinks(page);
  };

  const handleConfirmDeleteRegisterLink = async() => {
    const res = await dispatch(deleteTalentRegisterLink({id: deletingItem?.id}));
    if(res && res.payload && res.payload.success) {
      fetchTalentRegisterLinks(1);
    }
    setIsConfirmOpen(false);
  }

  const handleSort = (sortHeader: any) => {
    dispatch(handleSortByHeader(sortHeader));
  };

  const showCustomToast = ({title="メッセージ", message, variant="success", type="normal"}) => {
    enqueueSnackbar("I love snacks.", {
      content: (key) => (
        <CustomToast
          title={title}
          message={message}
          variant={variant}
          snackbarKey={key}
          closeSnackbar={closeSnackbar}
          type={type}
        />
      ),
    });
  }
  useEffect(() => {
    if(error) {
      showCustomToast({message: error, variant: "error"});
      dispatch(setError(null));
    }
    else if(success) {
      showCustomToast({message: success});
      dispatch(setSuccess(null));
    }
  }, [error, success]);

  useEffect(() => {
    if (talentOptionsRelatedData.talent_types) {
      dispatch(setTypeOptions(talentOptionsRelatedData.talent_types));
    }
  }, [talentOptionsRelatedData]);

  useEffect(() => {
    dispatch(getOptionsRelatedTalent(null));
  }, [])

  useEffect(() => {
    fetchTalentRegisterLinks(1);
  }, [sortBy, sortOrder])

  useEffect(() => {
    if (isFromTagsList && isOpenSearchTagsPanel) {
      setIsOpenRegisterPanel(true);
    }
  }, [location.state]);

  const handleSetSearchQuery = (value: string) => {
    dispatch(setSearchQuery(value));
  };

  const handleSearchRegisterLink = (event: any) => {
    if (event.key === "Enter") {
      fetchTalentRegisterLinks(1);
    }
  };

  const handleClearSearch = () => {
    handleSetSearchQuery("");
    fetchTalentRegisterLinks(1, "");
  }

  return (
    <>
      <CompanyHeader />
      <div className="mt-6 px-6 flex gap-6 relative">
        <CompanySideMenu />
        <div className="flex-1">
          <div className="mb-6 flex items-center gap-4">
            {!fromMenu && (
              <ReturnButton onClick={() => navigate('/company/talent', { state: { isFromDetail: true } })} />
            )}
            {!fromMenu && (
              <span className="text-sm alu-text-base-1">/</span>
            )}
            <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
              登録リンク管理
            </h3>
          </div>

          <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CustomButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openPanel("create")}
                  disabled={staffInfo?.type == STAFF_TYPE.GENERAL}
                >
                  登録リンク発行
                </CustomButton>
              </div>
              <div className='relative'>
                <TextInput
                  value={searchQuery}
                  onChange={(e: any) => handleSetSearchQuery(e.target.value)}
                  onKeyDown={(e: any) => handleSearchRegisterLink(e)}
                  onClearQuery={() => handleClearSearch()}
                  placeholder="検索"
                  searchIcon={true}
                  customClassName="!h-[28px] !w-[240px] !text-xs"
                  size={"filter"}
                />
              </div>
            </div>
          </div>

          <div className="bg-white relative mb-6 rounded-b-lg">
            <table className="table-auto w-full">
              <thead className="border-b alu-border px-4 py-2">
                <tr>
                  {tableHeaders.map((header, idx) => (
                    <th
                      key={idx}
                      className={`${idx === 0 ? "pl-4 pr-3" : "px-3"} py-2 text-left ${header.width}`}
                    >
                      <div 
                        className={`flex items-center ${header.isSort && "hover:cursor-pointer"}`}
                        onClick={() => header.isSort && handleSort(header)}
                      >
                        <p className={`text-sm leading-[1.3125] ${header.currentSortState !== 0 ? "font-bold underline underline-offset-4 alu-text-base-2" : "font-normal alu-text-base-1"}`}>{header.title}</p>
                        {header.isSort && (
                          <button className={`${header.currentSortState === 2 ? "rotate-180" : "rotate-0"}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" ><g clipPath="url(#clip0_8574_176)"><path d="M7 10L12 15L17 10H7Z" fill="#808080" /></g><defs><clipPath id="clip0_8574_176"><rect width="24" height="24" fill="white" /></clipPath></defs></svg>
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {registerLinkList?.length > 0 && registerLinkList.map((item) => (
                  <tr key={item.id} className={`${selectedRecord?.code === item?.code ? "outline outline-1 outline-[#FF861A]" : "border-t border-t-[#E0E0E0] hover:bg-[#fff3e5]"}`}>
                    <td className="pl-4 pr-3 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[102px]">
                    {!item.is_referral_link ?
                      <p
                        className="text-sm text-[#FF861A] hover:cursor-pointer hover:underline"
                        onClick={() => openPanel("edit", item)}
                      >
                        {item.code}
                      </p>
                      :
                      <>{item.code}</>
                    }
                    </td>
                    <td className="px-3 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[124px]">{item?.talent_role?.label || "ー"}</td>
                    <td className="px-3 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[224px]">
                      <div className="flex items-center gap-[0.625rem] flex-wrap">
                      {item.trl_tags?.length > 0 ? (
                        [...item.trl_tags]
                          .sort((a, b) => (a?.tag?.title || '').localeCompare(b?.tag?.title || ''))
                          .map((tag) => (
                            <span
                              key={tag?.id}
                              className="w-fit px-2 py-[0.125rem] text-left rounded-sm bg-[#F7F7F7] text-sm alu-text-base-2"
                            >
                              {tag?.tag?.title}
                            </span>
                          ))
                      ) : (
                        'ー'
                      )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-left font-normal text-sm leading-[1.3125] w-[354px]">
                      <p className="alu-text-base-2 break-words break-all">
                        {item?.description ? parse(item?.description) : "ー"}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-right font-normal text-sm leading-[1.3125] alu-text-base-2 w-[104px]">
                      {item?.number_registration > 0 ? <p className="text-[#FF861A] hover:underline text-right hover:cursor-pointer" onClick={() => redirectToTalentList(item)}>{item?.number_registration}</p> : "0"}
                    </td>
                    <td className="px-3 pr-5 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[92px]">{item?.public_date ? formatDate(item?.public_date) : "ー"}
                    </td>
                    <td className="px-3 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                      <div className="px-2 py-[0.375rem] rounded bg-[#F7F7F7] w-fit flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                          <circle cx="6" cy="6.5" r="6" fill={item?.is_enable_link ? "#06C270" : "#BBB"}/>
                        </svg>
                        {item?.is_enable_link ? "有効" : "無効"}
                      </div>
                    </td>
                    <td className="pl-3 pr-4 py-2 text-right font-normal text-sm leading-[1.3125] w-[74px]">
                      <CustomButton
                        onClick={() => {setDeletingItem(item); setIsConfirmOpen(true)}}
                        variant="text"
                        customStyle="ml-auto"
                        disabled={item?.number_registration || item.is_referral_link || staffInfo?.type == STAFF_TYPE.GENERAL}
                      >
                        削除
                      </CustomButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {countItems > pageSize && <div className="flex items-center justify-center mt-6">
            <CustomPagination
              total={countItems}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handleChangePage}
            />
          </div>}
        </div>
      </div>

      {isConfirmOpen && (
        <ConfirmPopup open={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          confirmContent={<p>登録リンクコード「{deletingItem?.code}」を削除します。<br/>よろしいですか？</p>}
          confirmDescription={""}
          onConfirm={handleConfirmDeleteRegisterLink}
          type="delete"
        />
      )}

      {isOpenRegisterPanel && (
        <div className="fixed right-2 top-2 bottom-2 z-[999]">
          <RegistrationLinkPanel onClose={handleClose} />
        </div>
      )}
      <Copyright view="company" />
    </>
  );
};

export default RegistrationLinkManagement;
